/* eslint-disable no-console */
import { IIamSettings } from "./iamSettings";
import { DriveStore } from "../../store/reducers";
import DriveApi from "../api";
import { ICredentials, ICredentialsWithServer } from "../credentialsStore";
import { UserService } from "../users";
import { TApiCredentialsCallback, TApiErrorCallback, TApiSuccessCallback } from "../api.interfaces";
import { IamOptimisticRefreshStatus, IamService } from "../../services/iamService";
import { CancelablePromise } from "../cancelablePromise";
import { getServerDate } from "../storage/date";
import constants, { SECONDS } from "../../constants";
import * as logger from "../logger";
import { IFeatureFlagsService } from "../../services/featureFlags";
import { shouldAddExtraLogging } from "../logger.internal";
import { MobileActions } from "../../store/mobile/actions";
import { getDatabaseNameFromUri } from "../databaseNameParsingUtilities";
import { unwrapFirstUser } from "./unwrapFirstUser";

const protectedPagesRegex = new RegExp(`(${ constants.IAM.PROTECTED_PAGES.map(p => p.replace("/", "\/")).join("|") })`);

export default class IamApi extends DriveApi {
    private tokenRefreshPromise: Promise<void> | null = null;
    private lastRefreshTime = new Date(0);
    private currentBackoffIndex: number | undefined = undefined;
    private readonly id: string;
    private refreshOnLaunch;

    constructor(
        userService: UserService,
        store: DriveStore,
        source: string,
        protected userName?: string,
        refreshOnLaunch = true,
        private readonly iamService?: IamService,
        readonly featureFlagService?: IFeatureFlagsService
    ) {
        super(userService, store, userName, featureFlagService);
        this.id = getServerDate().getTime() + "-" + source;
        this.refreshOnLaunch = refreshOnLaunch;
        if (shouldAddExtraLogging(logger.Source.IAM)) {
            logger.log(logger.Source.IAM, `IamApi created with id: ${ this.id } for user: ${ this.userName }`);
        }
        this.updateTransportWithHeaders();
    }

    call(method: string, params: any, callbackSuccess?: TApiSuccessCallback, callbackError?: TApiErrorCallback, ignoreCredentials?: boolean, binaryData?: ArrayBuffer, binaryMimeType?: string) {
        this.checkTokenExpiration(ignoreCredentials)
            .then(() => super.call(method, params, callbackSuccess, (async (error) => {
                // try to refresh here if fail, call forgot
                if (error.data.type === "InvalidUserException" && error.message === "Invalid session") {
                    await this.checkTokenExpiration();
                }
                callbackError && callbackError(error);
            }), ignoreCredentials, binaryData, binaryMimeType));
    }

    callPromise(method: string, params: any, ignoreCredentials?: boolean) {
        return super.callPromise(method, params, ignoreCredentials);
    }

    multiCall(calls: any[], callbackSuccess: TApiSuccessCallback, callbackError: TApiErrorCallback, ignoreCredentials = false) {
        this.checkTokenExpiration(ignoreCredentials)
            .then(() => super.multiCall(calls, callbackSuccess, (async (error) => {
                if (error.data.type === "InvalidUserException" && error.message === "Invalid session") {
                    await this.checkTokenExpiration();
                }
                callbackError(error);
            }), ignoreCredentials));
    }

    multiCallPromise(calls: any[], ignoreCredentials = false) {
        return super.multiCallPromise(calls, ignoreCredentials);
    }

    getSession(successCallback: TApiCredentialsCallback | null, newSession?: boolean): CancelablePromise<ICredentialsWithServer> {
        return new CancelablePromise(this.checkTokenExpiration())
            .then(() => super.getSession(successCallback, newSession));
    }

    updateTransportWithHeaders(credentials?: ICredentials): void {
        const creds = credentials ?? this.getCurrentCredentials()?.credentials;
        if (creds && creds.iamJwt && creds.userName && creds.database) {
            this.setHeaders([
                { name: "Authorization", value: `Bearer ${ creds.iamJwt }` },
                { name: "UserName", value: creds.userName },
                { name: "Database", value: creds.database}
            ]);
        }
    }

    setUser(credentials: ICredentials, server: string, iamSettings?: IIamSettings) {
        super.setUser(credentials, server, iamSettings);
        this.updateTransportWithHeaders(credentials);
    }

    getUserDatabaseNames(userName: string, iamJwt: string): Promise<string[]> {
        // Database is not optional in ICredentials, but we don't know it yet, so we pass a dummy value
        // GetDatabaseNames does not require the database in the header, but doesn't break if it's there
        this.updateTransportWithHeaders({ userName, database: "_no_database_", iamJwt});
        return this.callPromise("GetDatabaseNames", {});
    }

    callUpsertUser(userName: string, iamJwt: string, database: string) {
        this.updateTransportWithHeaders({ userName, database, iamJwt});
        return this.callPromise("UpsertMyAdminUser", { userName });
    }

    checkUserExist(userName: string, iamJwt: string, database: string) {
        this.updateTransportWithHeaders({ userName, database, iamJwt});
        return this.callPromise("Get", {
            typeName: "User",
            search: { name: userName }
        }).then(unwrapFirstUser);
    }

    allowToLogout() {
        const mobileStore = this.store.getState().mobile;
        const currentPage = mobileStore.currentPage || "";
        const isLocked = mobileStore.pageState.isLocked;
        const userName = this.getUser()?.name || "";

        if (userName && !this.userService.isDriverUsingUI(userName)) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: User not using the UI, not allowed to logout`, this.getUser());
            return false;
        }

        if (isLocked) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: Lock-screen is up, not allowed to logout`, this.getUser());
            return false;
        }

        if (protectedPagesRegex.test(currentPage)) {
           logger.log(logger.Source.IAM, `IamApi.allowToLogout: User on a protected page, not allowed to logout`, this.getUser());
           return false;
        }

        if (this.store.getState().data.driving) {
            logger.log(logger.Source.IAM, `IamApi.allowToLogout: User is currently driving, not allowed to logout`, this.getUser());
            return false;
        }

        return true;
    }

    private async refreshToken(uniqueCallId: string) {
        if (shouldAddExtraLogging(logger.Source.IAM)) {
            logger.log(logger.Source.IAM, `IamApi.refreshToken: calling iamService.silentSignIn. ${ uniqueCallId }`, this.userName);
        }
        try {
            this.userName && this.store.dispatch(MobileActions.setIamRefreshState({ key: this.userName, isRefreshing: true }));
            await this.iamService?.silentSignIn(this, uniqueCallId);
            this.refreshOnLaunch = false; // Disable refresh on launch after the first successful refresh
        } catch (error) {
            // if we failed due to failing a refresh with keycloak,
            // then we should forgot the credentials and log the user out
            if (error
                && error.message
                && constants.IAM.ERROR.find(e => error.message.includes(e))) {
                if (!this.allowToLogout()) {
                    this.userName && this.store.dispatch(MobileActions.setIamSessionInvalidatedState({ key: this.userName, isInvalidated: true }));
                } else {
                    this.userName && this.store.dispatch(MobileActions.setIamSessionInvalidatedState({ key: this.userName, isInvalidated: false }));
                    this.forget();
                }
            }
        } finally {
            this.lastRefreshTime = getServerDate();
            this.userName && this.store.dispatch(MobileActions.setIamRefreshState({ key: this.userName, isRefreshing: false }));
        }
    }

    private async checkTokenExpiration(ignoreCredentials?: boolean): Promise<void> {
        let uniqueCallId = `${ this.id }-${ getServerDate().getTime() }`;
        let databaseNameFromUri = getDatabaseNameFromUri(window.location);
        const creds = this.getCurrentCredentials();

        // we shouldn't try make any refresh calls if:
        // - IamService is undefined,
        // - if we are ignoring credentials
        // - URL does not contain a database name, happens when user opens app before we determine the database name and redirect to correct URL
        // - or if we don't have credentials
        if (!this.iamService || ignoreCredentials || databaseNameFromUri === "" || !creds) {
            if (shouldAddExtraLogging(logger.Source.IAM) && databaseNameFromUri === "") {
                logger.log(logger.Source.IAM, `iamApi.checkTokenExpiration: No database name in URL, skipping token refresh ${ uniqueCallId }`, this.getUser());
            }

            return Promise.resolve();
        }

        // Do not attempt to refresh the token if the session is already invalidated
        if (this.userName && this.store.getState().mobile.iamSessionInvalidated[this.userName]){
            if (this.allowToLogout()) {
                this.forget();
                this.store.dispatch(MobileActions.setIamSessionInvalidatedState({ key: this.userName, isInvalidated: false }));
            }

            return Promise.resolve();
        }
        const currentBackoff = this.currentBackoffIndex === undefined ? 0 : constants.IAM.BACKOFF_TIME_VALUES[this.currentBackoffIndex] * SECONDS;

        // We will only attempt to refresh if the following condition are met
        // - We are not already refreshing the token (lock)
        // - We are past the refresh debounce time plus the current backoff
        if (!this.tokenRefreshPromise &&
            this.lastRefreshTime.getTime() + constants.IAM.REFRESH_DEBOUNCE_TIME + currentBackoff < getServerDate().getTime()) {
            this.lastRefreshTime = getServerDate();
            this.tokenRefreshPromise = (async () => {
                try {
                    let result = await this.iamService?.shouldOptimisticRefresh(creds, uniqueCallId, this.refreshOnLaunch);
                    if (result === IamOptimisticRefreshStatus.PROCEED_TO_REFRESH) {
                        this.currentBackoffIndex = undefined;
                        await this.refreshToken(uniqueCallId);
                    } else if (result === IamOptimisticRefreshStatus.KEYCLOAK_SERVER_ERROR_ENCOUNTERED) {
                        // Get the next backoff time from the constants.IAM.BACKOFF_TIME_VALUES array
                        this.currentBackoffIndex = this.currentBackoffIndex === undefined
                            ? 0
                            : Math.min(this.currentBackoffIndex + 1, constants.IAM.BACKOFF_TIME_VALUES.length - 1);
                    }
                } catch (error) {
                    logger.error(logger.Source.IAM, `iamApi.checkTokenExpiration error: ${ error } ${ uniqueCallId }`, this.getUser());
                } finally {
                    this.tokenRefreshPromise = null;
                }
            })();
        }

        return this.tokenRefreshPromise ? this.tokenRefreshPromise : Promise.resolve();
    }
}